import React, { useState, useEffect } from 'react';
import { X, Gift } from 'lucide-react';
import { useDirection } from '@/providers/DirectionProvider';

interface DiscountBannerProps {
  amount: number;
  message: string;
  onDismiss?: () => void;
}

export const DiscountBanner: React.FC<DiscountBannerProps> = ({
  amount,
  message,
  onDismiss
}) => {
  const { direction } = useDirection();
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Check if banner was dismissed in this session
  useEffect(() => {
    const sessionDismissed = sessionStorage.getItem('discountBannerDismissed');
    if (!sessionDismissed) {
      // Animate in after a short delay
      setTimeout(() => setIsVisible(true), 500);
    } else {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setDismissed(true);
      sessionStorage.setItem('discountBannerDismissed', 'true');
      onDismiss?.();
    }, 300);
  };

  if (dismissed) return null;

  return (
    <div
      className={`
        bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500
        text-white
        py-3 px-4
        text-center
        relative
        shadow-lg
        transition-all duration-300 ease-in-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'}
      `}
      dir={direction}
    >
      <div className="flex items-center justify-center gap-2">
        <Gift className="h-5 w-5 animate-bounce" />
        <span className="font-medium text-sm sm:text-base">
          {message}
        </span>
      </div>

      <button
        onClick={handleDismiss}
        className={`
          absolute top-1/2 -translate-y-1/2
          ${direction === 'rtl' ? 'left-3' : 'right-3'}
          hover:bg-white/20
          rounded-full
          p-1.5
          transition-colors
        `}
        aria-label="סגור"
      >
        <X size={18} />
      </button>
    </div>
  );
};

export default DiscountBanner;
